'use client';

import React from 'react';

import { useTranslations } from 'next-intl';

import { AppleButton } from '@/components/AppleButton';
import { decodeJWT } from '@/modules/auth/decodeJWT';
import {
  type AppleSignInResponse,
  type SSOButtonVariant,
} from '@/modules/auth/types';
import { useRedirectQueryParam } from '@/modules/auth/useRedirectQueryParam';
import { fetchAppleCredentials } from '@/modules/login/api';
import {
  handleSSOError,
  setAccountLinkingCookies,
} from '@/modules/login/helpers';
import { LoginType } from '@/modules/login/types';
import { useLoginMutations } from '@/modules/login/useLoginMutations';
import { useExtendedRouter } from '@/modules/routing/useExtendedRouter';
import { useSSOSignup } from '@/modules/signup/hooks/useSSOSignup';
import { useSignupTracking } from '@/modules/signup/hooks/useSignupTracking';
import { SignupType } from '@/modules/signup/types';

type Props = {
  ssoButtonVariant: SSOButtonVariant;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setError: React.Dispatch<React.SetStateAction<string>>;
};

function AppleSignupButton({
  ssoButtonVariant,
  setError,
  setIsLoading,
}: Props) {
  const t = useTranslations('signup');
  const { sendSignupSubmitActionEvent } = useSignupTracking();
  const { signupWithSSO } = useSSOSignup({
    ssoType: SignupType.Apple,
  });
  const { appleLoginMutation } = useLoginMutations({
    loginType: LoginType.AppleSignup,
    setError,
  });
  const router = useExtendedRouter();
  const redirectQueryParam = useRedirectQueryParam();

  async function handleAppleIDResponse(
    credentials: AppleSignInResponse,
    redirect_uri: string
  ) {
    let apple_user_id: string;
    let apple_access_token: string;

    setIsLoading(true);

    try {
      ({
        data: { apple_user_id, apple_access_token },
      } = await fetchAppleCredentials({
        code: credentials.authorization.code,
        redirect_uri,
      }));
    } catch {
      return setError(t('Error.SocialProvider', { socialProvider: 'Apple' }));
    }

    const { email, name } = credentials.user
      ? credentials.user
      : decodeJWT(credentials.authorization.id_token);

    function handleUserNotFound() {
      return signupWithSSO({
        email,
        credentials: { apple_user_id, apple_access_token },
        name,
      });
    }

    function handleAccountNotLinked() {
      setAccountLinkingCookies({ apple_user_id, apple_access_token }, email);

      return router.push(`/login/${redirectQueryParam}`);
    }

    appleLoginMutation.mutate(
      {
        apple_access_token,
        apple_user_id,
      },
      {
        onError: (error) => {
          handleSSOError({
            error,
            setError,
            onUserNotFound: handleUserNotFound,
            onAccountNotLinked: handleAccountNotLinked,
          });
        },
      }
    );
  }

  function handleSubmitTracking() {
    sendSignupSubmitActionEvent(SignupType.Apple);
  }

  return (
    <AppleButton
      variant={ssoButtonVariant}
      handleAppleIDResponse={handleAppleIDResponse}
      handleSubmitTracking={handleSubmitTracking}
    />
  );
}

export { AppleSignupButton };
